import { Flex, Text, useMantineTheme } from "@mantine/core";
import { useRef, useState, useLayoutEffect } from "react";
import { BadgeInstruments } from "../Badge";
import { AddCircle } from "iconsax-react";
import { IColor } from "../../utils/selectColors";
import { useTranslation } from "react-i18next";
import InstrumentsModal from "./InstrumentsModal";

type IProps = {
  instruments?: string[];
  hasColor?: boolean;
  idProduct: number | undefined;
  isCripto: boolean;
};

export default function Instruments({
  instruments,
  hasColor = true,
  idProduct,
  isCripto = false,
}: IProps) {
  const theme = useMantineTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [visibleCards, setVisibleCards] = useState<number>(0);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    let raf: number;

    const calculateVisibleCards = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        if (containerWidth === 0) {
          // Tenta de novo no próximo frame
          raf = requestAnimationFrame(calculateVisibleCards);
          return;
        }
        const cardWidth = 150;
        const maxCards = Math.floor(containerWidth / cardWidth);
        setVisibleCards(maxCards);
      }
    };

    const observer = new window.ResizeObserver(calculateVisibleCards);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Chama uma vez para garantir
    calculateVisibleCards();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [instruments]);

  return !!instruments && instruments.length > 0 ? (
    <>
      {open ? (
        <InstrumentsModal
          idProduct={idProduct}
          isOpen={open}
          onClose={() => setOpen(false)}
          isCripto={isCripto}
        />
      ) : null}
      <Flex w="100%" direction={"column"}>
        <Text
          ff="Montserrat"
          fz="14px"
          lh="32px"
          fw="700"
          c={hasColor ? "basic.5" : "white"}
          style={{ whiteSpace: "nowrap" }}
        >
          {isCripto
            ? t("global.instruments.label_crypto")
            : t("global.instruments.label")}
        </Text>
        <Flex
          ref={containerRef}
          w={hasColor ? "100%" : { base: "100%", 1200: "50%" }}
          justify="end"
          h="40px"
          align={"center"}
          pos={"relative"}
          sx={{ overflow: "hidden" }}
        >
          <Flex pos={"absolute"} w="100%" h="100%" align={"center"} gap="24px">
            {instruments
              ?.slice(0, visibleCards)
              .map((instrument, index: number) => {
                return (
                  <BadgeInstruments
                    key={`${instrument}${index}`}
                    badgeColor={hasColor ? IColor.babyBlue : IColor.incolor}
                    badgeName={`${instrument}`}
                  />
                );
              })}
          </Flex>
          {instruments.length > visibleCards ? (
            <Flex
              h={"40px"}
              w={"170px"}
              px="24px"
              align={"center"}
              justify={"end"}
              sx={{ zIndex: 1 }}
              bg={
                hasColor
                  ? "linear-gradient(90deg, rgba(0,65,255,0) 0%, rgba(255,255,255,0.7399334733893557) 5%, rgba(255,255,255,1) 30%)"
                  : theme.colors.other[3]
              }
            >
              <Flex
                onClick={() => setOpen(true)}
                style={{
                  cursor: "pointer",
                }}
                gap={"8px"}
              >
                <Text
                  color={hasColor ? theme.colors.basic[4] : "white"}
                  fz={"14px"}
                  fw={600}
                  style={{ whiteSpace: "nowrap" }}
                  ff={"barlow"}
                >
                  {t("global.instruments.see_all")}
                </Text>
                <AddCircle
                  size={24}
                  color={hasColor ? theme.colors.basic[2] : "white"}
                />
              </Flex>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </>
  ) : null;
}
